// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-02-24 00:34:07 +0100 (Mon, 24 Feb 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 150462 $
//  Purpose   : rules for tree
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon} from 'semantic-ui-react'

// ---Framework imports
import {isFunction} from 'ldash'
import {Actions} from 'uix/registries'

import log from 'uix/logging'
import {
  dict,
  get,
  flatten,
  keys,
  mapItems,
  has,
  sorted,
  filterItems,
  reversed,
  isArray,
  values,
  isNil,
} from 'ldash'
import {getSubdomains, getAncestorDomains} from 'uix/selectors'
import {VIcon} from 'graphics'
import {encodeSlimLink} from 'uix/utils'
import {DomainIcon} from 'uix/domain/icon'

// ---Local imports
import {SEP, ID, ROOT_ID, dummyChildren, getExpansionState} from './utils'
import {tabsForTreeNode} from './tabrules'
import {ntypes, CONTAINER_LABELS, NTYPE} from './rule'

// ---Globals
// XXX: we should probably rename the L131Split layout to L131Tree

// datatypes that support initial tree autoexpand + autofocus
export const DATATYPES_EXPAND_LOOKUP = [0, 1, 2, 3, 7, 9, 13, 14, 16, 18, 19, 21, 22, 23]

export {NTYPE, dummyChildren}

const img = (imgSet, name, cname) => <img key={name} className={cname} src={imgSet[name]} />

export const buildStaticDomainNodes = (rules, dnode) => {
  /* build all static nodes (only first layer atm) given a domain node
  XXX: share more stuff with buildTreeStructure?
  */
  let staticTypes = values(NTYPE).filter(
    nodeType => nodeType.startsWith('domain:') && (nodeType.match(/\:/g) || []).length == 1
  )
  let flatNodes = {[NTYPE.DOMAIN]: [dnode]}
  return staticTypes
    .map(
      nodeType =>
        // really take first?
        rules[nodeType].buildNodes(flatNodes).filter(node => node.nodeType == nodeType)[0]
    )
    .filter(e => e)
}

export const ancestorAxis2TreeAxis = (ancestorAxis, domains) => {
  /* transform ancestor axis to tree nodes for initial expand
  order will be reversed - last item is leaf node
  see DATATYPES_EXPAND_LOOKUP
  */
  let {odid, datatype, subtype} = ancestorAxis[0]
  let dname = domains.odid2name[odid]
  let result = []
  result.push({id: ID(dname), nodeType: NTYPE.DOMAIN})

  let isContainerNode = r => r.datatype == 7
  let container = reversed(ancestorAxis).filter(isContainerNode)[0]
  if (container) {
    if (container.subtype == 14) {
      var idContext = ID(dname, 'deliverer')
      result.push({id: idContext, nodeType: NTYPE.DOMAIN_DELIVERER, idContext, odid, otid: 0})
    } else if ([1, 2, 18, 21, 22, 23].includes(container.subtype)) {
      // XXX: only some 7/x types in are in NTYPE.DOMAIN_DEV
      var idContext = ID(dname, 'dev')
      result.push({id: idContext, nodeType: NTYPE.DOMAIN_DEV, idContext, odid, otid: 0})
    } else {
      var idContext = ID(dname, 'indices')
      result.push({id: idContext, nodeType: NTYPE.DOMAIN_INDICES, idContext, odid, otid: 0})
    }
  } else {
    // + static all id
    var idContext = ID(dname, 'all')
    result.push({id: idContext, nodeType: NTYPE.DOMAIN_ALL, idContext, odid, otid: 0})
  }

  for (let r of reversed(ancestorAxis)) {
    let {otid, otparent, datatype, slimlink} = r
    let id = slimlink ? ID(idContext, encodeSlimLink(otparent, otid)) : ID(idContext, otid)
    result.push({id, nodeType: NTYPE.VLTYPE, idContext, otid, odid})
  }
  return result
}

const buildDomainNode = domain => ({
  ...domain,
  // move
  datatype: 10,
  subtype: domain.type,
  odid: domain.id,
  // new node props
  title: domain.isGhost ? domain.name : domain.description || domain.name,
  nodeType: NTYPE.DOMAIN,
  id: ID(domain.name),
  dname: domain.name,
})

const buildDomainNodes = (iconfig, domains, loadedDomains, user) => {
  /* create tree structure of domain nodes
   */
  let subdomains = getSubdomains(domains.rootDomain, domains)

  // triggers if current domain = instance rootDomain
  // XXX: unclear domain-loading semantics
  if (domains.rootDomain === iconfig.domain.rootDomain) {
    subdomains = values(domains.data)
  }

  // filter root ghostdomains
  subdomains = subdomains.filter(d => !(d.isGhost && d.parent === null))

  // filter by viewTree user group
  subdomains = subdomains.filter(d => user.hasGroup('viewTree', d.name))

  // set of active dnames
  let availableDomainNodes = new Set(subdomains.map(d => d.name))

  // should domain use ROOT_ID parent
  let isRootDomainNode = d => {
    if (false) {
      // full flat
      return true
    } else if (!d.parent) {
      return true
    } else if (!availableDomainNodes.has(d.parent)) {
      return true
    }
    return false
  }

  let domainNode = d => {
    let loaded = loadedDomains[d.name] ? true : undefined
    return {
      ...buildDomainNode(d),
      parentId: isRootDomainNode(d) ? ROOT_ID : ID(d.parent),
      loaded,
      children: loaded ? undefined : dummyChildren,
    }
  }

  return sorted(subdomains, d => d.description || d.name).map(domainNode)
}

export const getUnitializedRootDomains = (iconfig, domains, loadedDomains, user) =>
  buildDomainNodes(iconfig, domains, loadedDomains, user)
    .filter(
      n => n.parentId === ROOT_ID && !loadedDomains[n.dname] && !domains.data[n.dname].isGhost
    )
    .map(n => n.dname)

const isVlmEnabled = (cfgopt, dname, domains, loadedDomains) =>
  !domains.data[dname].isGhost && get(loadedDomains[dname], `dconfig.vlm.tree.${cfgopt}`)

const getStaticDomainNodeChildren = (getChildrenParams = {}) => node =>
  Actions.getChildren({otid: 0, odid: node.odid, idContext: node.idContext, ...getChildrenParams})

const _buildVlType = (
  data,
  treeDetailsById,
  treeChildData,
  wasExpanded,
  idContext,
  parentId,
  translateType
) => {
  let {otid, slimlink} = data
  let details = treeDetailsById[otid]

  let nodeid = ID(idContext, slimlink || otid)

  let empty = !details || !details.haschildren
  // initializing with false here ensures getIndicators() show's a loader
  let loaded = get(treeChildData, `${idContext}.${otid}`, false)
  if (slimlink) {
    // linked leaf nodes and cannot be expanded
    loaded = true
  }

  if (wasExpanded[nodeid] === false) {
    // if collapsed, set loaded to false
    loaded = false
  }

  let {children} = getExpansionState(loaded, empty)

  return {
    nodeType: NTYPE.VLTYPE,
    id: nodeid,
    idContext,
    otid,
    odid: data.odid,
    parentId: parentId == 0 ? idContext : ID(idContext, parentId),
    title: data.caption || translateType(data.odid, data.datatype, data.subtype),
    otlink: data.otlink ? data.otlink : null,
    odlink: data.odlink ? data.odlink : null,
    slimlink,
    delstate: data.delstate,
    loaded,
    children,
  }
}

// input is a subset of VTree.props
export const rules = ({
  allClassCfgs, // only for tabs()
  iconfig, // only for tabs()
  request, // only for tabs()
  Graphics,
  domains,
  loadedDomains,
  i18n,
  treeById,
  treeChildData,
  treeAvailableTypes,
  treeAvailableStates,
  treeDetailsById,
  statisticData,
  user,
  wasExpanded = {},
  ...ruleProps // data from storeMappers
}) => {
  //~ log.x(statisticData)
  let language = get(i18n, '_language', 'de')

  let buildStaticDomainNode = (nodeType, parentNode, title, loaded, extra) => {
    /* like 'domain:all' (Alle Inhalte)
     */
    if (!nodeType.startsWith('domain:')) {
      throw new Error(`no domain node type ${nodeType}`)
    }
    let dname = parentNode.dname
    // derive nodeId from nodeType
    let id = ID(dname, nodeType.slice(7))

    if (loaded === undefined) {
      loaded = true
    }
    return {
      nodeType: nodeType,
      id,
      idContext: id,
      parentId: parentNode.id,
      odid: parentNode.odid,
      dname,
      color: parentNode.color,
      title,
      loaded,
      children: loaded ? undefined : dummyChildren,
      // custom extra data (list of otids, probably children)
      extra,
    }
  }
  // we split domain:indices and domain:dev here
  const containerNodeHasData = (dname, nodeType) =>
    has(treeChildData, `${ID(dname, nodeType.split(':')[1])}.0`)
  const buildContainer = (nodeType, containerTypes, isEnabled) => ({
    buildAfter: NTYPE.DOMAIN,
    isEnabled,
    tabs: getTabsForNode,
    getChildren: node =>
      Actions.getChildren({
        otid: 0,
        odid: node.odid,
        idContext: node.idContext,
        typeName: containerTypes.map(n => `${n}_domain_container`).join(','),
      }),
    buildNodes: flatNodes =>
      flatNodes[NTYPE.DOMAIN].map(dnode =>
        buildStaticDomainNode(
          nodeType,
          dnode,
          i18n.get(CONTAINER_LABELS[nodeType]),
          containerNodeHasData(dnode.dname, nodeType)
        )
      ),
  })

  const buildVlType = ({otid, idContext = null, parentId = 0, translateType = () => null}) =>
    _buildVlType(
      treeById[otid],
      treeDetailsById,
      treeChildData,
      wasExpanded,
      idContext,
      parentId,
      translateType
    )

  let getTabsForNode = node => {
    // if deleted or configs not loaded
    let DEFAULT_FALLBACK = [['tree', 'id']]
    if (user?.groups.includes('debug')) {
      DEFAULT_FALLBACK.push(['tree', 'dbid'])
    }
    let {otid, odid, nodeType, slimlink} = node
    let ddata = loadedDomains[odid]

    if (nodeType == NTYPE.DOMAIN) {
      // custom handling for domain nodes
      if (!ddata) {
        return DEFAULT_FALLBACK
      }
      let tconfig = ddata.types.byId[10][node.subtype]
      return tabsForTreeNode(
        request,
        iconfig,
        allClassCfgs[ddata.name],
        ddata.dconfig,
        {tconfig},
        node
      )
    } else {
      // all other
      let data_id = slimlink || otid
      let record = {}

      if (nodeType === NTYPE.VLTYPE) {
        // custom handling for vltype nodes
        let treeData = treeById[data_id]
        ddata = loadedDomains[treeData.odlink || treeData.odid]
        let tconfig = ddata?.types.byId[treeData.datatype]?.[treeData.subtype]
        if (node.delstate || !tconfig) {
          return DEFAULT_FALLBACK
        }
        record = {tconfig, nodeData: treeData, details: treeDetailsById[otid]}
      }

      if (!ddata) {
        return DEFAULT_FALLBACK
      }

      return tabsForTreeNode(
        request,
        iconfig,
        allClassCfgs[ddata.name],
        ddata.dconfig,
        record,
        node
      )
    }
  }

  ruleProps = {
    ...ruleProps,
    i18n,
    user,
    domains,
    loadedDomains,
    treeById,
    treeDetailsById,
    treeChildData,
    _buildVlType,
    buildVlType,
    buildStaticDomainNode,
    containerNodeHasData,
    isVlmEnabled,
    treeAvailableStates,
    treeAvailableTypes,
    Graphics,
    wasExpanded,
  }

  return {
    default: {
      buildNodes: () => [{id: ROOT_ID, nodeType: 'ROOT'}],
      order: 0,
      icon: ({node, colorizeDomains}) => {
        let color = colorizeDomains ? node.color : undefined
        if (node.expanded) {
          return <VIcon name="folder open outline" style={{color}} />
        } else {
          return <VIcon name="folder outline" style={{color}} />
        }
      },
    },

    //~ [NTYPE.SYS]: {
    //~ icon: ({node}) => img(Graphics.tree, 'administration', 'treeIcon'),
    //~ tabs: node => keys(classCfgs['sys'].tabs).map(tabname => ['sys', tabname]),
    //~ buildNodes: () => [],
    //~ },

    [NTYPE.DOMAIN]: {
      // XXX: this smells
      buildSingleNode: ({dname}) => buildDomainNode(domains.data[dname]),
      getChildren: node =>
        loadedDomains && !loadedDomains[node.id] && !domains.data[node.id].isGhost
          ? Actions.initDomain(node.id, 'tree children')
          : null,
      icon: ({node, colorizeDomains}) => <DomainIcon dname={node.dname} />,
      tabs: getTabsForNode,
      buildNodes: () => buildDomainNodes(iconfig, domains, loadedDomains, user),
    },

    // Topologie
    [NTYPE.DOMAIN_ALL]: {
      isEnabled: node => isVlmEnabled('allcontents', node.dname, domains, loadedDomains),
      getChildren: getStaticDomainNodeChildren(),
      tabs: getTabsForNode,
      buildAfter: NTYPE.DOMAIN,
      buildNodes: flatNodes =>
        flatNodes[NTYPE.DOMAIN].map(dnode =>
          buildStaticDomainNode(
            NTYPE.DOMAIN_ALL,
            dnode,
            i18n.get('tnode_all'),
            has(treeChildData, `${ID(dnode.dname, 'all')}.0`)
          )
        ),
    },
    [NTYPE.DOMAIN_PAPERBIN]: {
      buildAfter: NTYPE.DOMAIN,
      order: 100,
      isEnabled: node => isVlmEnabled('paperbin', node.dname, domains, loadedDomains),
      tabs: getTabsForNode,
      getChildren: getStaticDomainNodeChildren({
        deleted: 'only',
        descendants: true,
        typeName: ['_metadata_', '_place_', '_name_', '_topstructure_'],
        links: false,
      }),
      buildNodes: flatNodes =>
        flatNodes[NTYPE.DOMAIN].map(dnode =>
          buildStaticDomainNode(
            NTYPE.DOMAIN_PAPERBIN,
            dnode,
            i18n.get('tnode_paperbin'),
            has(treeChildData, `${ID(dnode.dname, 'paperbin')}.0`)
          )
        ),
    },
    // Typen
    [NTYPE.DOMAIN_BYTYPE]: {
      tabs: getTabsForNode,
      ...ntypes.DOMAIN_BYTYPE(ruleProps),
    },

    [NTYPE.BYTYPE_TYPE]: {
      tabs: getTabsForNode,
      ...ntypes.BYTYPE_TYPE(ruleProps),
    },

    // States
    [NTYPE.DOMAIN_BYSTATE]: {
      tabs: getTabsForNode,
      ...ntypes.DOMAIN_BYSTATE(ruleProps),
    },
    [NTYPE.BYSTATE_AXIS]: {
      tabs: getTabsForNode,
      ...ntypes.BYSTATE_AXIS(ruleProps),
    },

    [NTYPE.BYSTATE_STATE]: {
      tabs: getTabsForNode,
      ...ntypes.BYSTATE_STATE(ruleProps),
    },

    // deliverer/publisher (epflicht)
    [NTYPE.DOMAIN_DELIVERER]: {
      tabs: getTabsForNode,
      ...ntypes.DOMAIN_DELIVERER(ruleProps),
    },
    [NTYPE.DELIVERER_YEAR]: {
      tabs: getTabsForNode,
      ...ntypes.DELIVERER_YEAR(ruleProps),
    },
    // Indices
    [NTYPE.DOMAIN_INDICES]: buildContainer(
      NTYPE.DOMAIN_INDICES,
      ['names', 'places', 'subjects'],
      node => {
        let ddata = loadedDomains[node.dname]
        return (
          !domains.data[node.dname].isGhost &&
          ddata &&
          (ddata.dconfig.names.on || ddata.dconfig.places.on || ddata.dconfig.subjects.on)
        )
      }
    ),
    // Development: wiki_domain_container ... presentation_domain_container
    [NTYPE.DOMAIN_DEV]: buildContainer(
      NTYPE.DOMAIN_DEV,
      ['wiki', 'webresource', 'code', 'presentation', 'workshops', 'blogs'],
      node => !domains.data[node.dname].isGhost && loadedDomains[node.dname]
    ),

    // pinned nodes
    [NTYPE.DOMAIN_PINNED]: ntypes.DOMAIN_PINNED(ruleProps),

    [NTYPE.VLTYPE]: {
      tabs: getTabsForNode,
      ...ntypes.VLTYPE(ruleProps),
    },
  }
}

/* Store Mapping (for VTree) */
const storeMappers = values(rules({}))
  .map(cfg => cfg.storeMap)
  .filter(e => e)

// merge result of all storeMappers into a dict
export const nodeRulesStore = root =>
  storeMappers.reduce((prev, storeMap) => ({...prev, ...storeMap(root)}), {})
